import type { App } from 'vue'
import { isRunningInRNWebView, supportsSnsAuthSession } from '@/utils/rn-bridge'

/**
 * 브라우저 오류 수집 (2026-10-29)
 *
 * 왜 있나 — 운영 테스트 중 화면 오류(스크립트 예외·화면 조각 로딩 실패)는 사용자 브라우저 콘솔에만 남고
 * 서버 로그로는 한 줄도 보이지 않았다. 아래 4가지를 서버(`POST /api/client-error`)로 보내
 * 운영 로그에 `[ClientError]` 한 줄로 남긴다.
 *   vue    — 컴포넌트 렌더·이벤트 처리 중 예외 (app.config.errorHandler)
 *   error  — 그 밖의 스크립트 예외 (window error)
 *   promise — 아무도 받지 않은 Promise 실패 (unhandledrejection) — API 실패(axios)는 서버 로그에 이미 있어 뺀다
 *   chunk  — 화면 조각(js) 로딩 실패 (vite:preloadError) — 배포 직후 옛 화면에서 주로 난다
 *
 * ⛔ 주소의 쿼리(?…)는 보내지 않는다 — SNS 콜백 주소에 토큰이 실린다.
 * 개발 서버(vite dev)에서는 보내지 않는다. 같은 오류는 한 번만, 한 화면 세션에 최대 10건.
 */

const MAX_PER_SESSION = 10
const sent = new Set<string>()

function endpoint(): string {
  return `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/client-error`
}

function buildId(): string {
  const s = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')
  return s?.src.split('/').pop() ?? ''
}

function appKind(): string {
  if (!isRunningInRNWebView()) return 'web'
  return supportsSnsAuthSession() ? 'app' : 'app-old'
}

function report(type: string, err: unknown, extra = ''): void {
  if (import.meta.env.DEV || sent.size >= MAX_PER_SESSION) return
  const e = err as { message?: unknown; stack?: unknown } | null
  const message = String((e && e.message) ?? err ?? '').slice(0, 500)
  const key = `${type}|${message}|${extra}`
  if (sent.has(key)) return
  sent.add(key)

  let userSeq = ''
  try {
    userSeq = localStorage.getItem('userSeq') ?? ''
  } catch {
    /* v12-ok: localStorage 차단 — 회원 번호 없이 보낸다 */
  }

  const body = JSON.stringify({
    type,
    message,
    source: extra,
    stack: typeof e?.stack === 'string' ? e.stack.slice(0, 1500) : '',
    route: location.pathname,
    build: buildId(),
    app: appKind(),
    userSeq,
  })
  try {
    // text/plain = 사전 요청(preflight) 없이 나간다 · keepalive = 페이지를 떠나는 중에도 보낸다
    void fetch(endpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body,
      keepalive: true,
    }).catch(() => {
      /* v12-ok: 오류 보고 자체의 실패는 사용자에게 알리지 않는다 */
    })
  } catch {
    /* v12-ok: fetch 미지원 등 — 보고만 건너뛴다 */
  }
}

export function installClientErrorReporter(app: App): void {
  app.config.errorHandler = (err, _instance, info) => {
    console.error(err)
    report('vue', err, info)
  }

  window.addEventListener('error', (ev) => {
    // 이미지·스크립트 태그의 로딩 실패는 ev.error 가 없다 — 이미지는 main.ts 가 따로 처리한다
    if (!ev.error) return
    report('error', ev.error, `${ev.filename?.split('/').pop() ?? ''}:${ev.lineno}:${ev.colno}`)
  })

  window.addEventListener('unhandledrejection', (ev) => {
    const r = ev.reason as { isAxiosError?: boolean } | null
    if (r && r.isAxiosError) return
    report('promise', ev.reason)
  })

  window.addEventListener('vite:preloadError', (ev) => {
    report('chunk', (ev as Event & { payload?: unknown }).payload)
  })
}
